/**
 * Presentation helpers for the conferência-time screen (#406).
 *
 * Kept out of the page so the numbers a salesperson will quote are formatted
 * by something with a test, not by JSX.
 */

import type { StagePositions } from './api.ts'

const MINUTE = 60_000

/**
 * What entering and leaving the bench mean, in the positions the account's
 * bench was read as (#673). Without them -- the report has not answered yet
 * -- the same sentence with no numbers, rather than another pharmacy's.
 */
export function benchNote(positions: StagePositions | undefined): string {
  const entry =
    positions === undefined ? '' : ` (a partir da posição ${positions.conferencia} do ERP)`
  const exit = positions === undefined ? '' : ` (a partir da posição ${positions.producao})`
  return (
    `Entrada: primeira leitura na conferência${entry}. ` +
    `Saída: primeira leitura na produção${exit}. ` +
    '“Com a leitura da IA”: nossa conferência estava pronta antes de a requisição sair da bancada.'
  )
}

/**
 * A duration as somebody at the bench would say it: minutes under an hour,
 * hours and minutes above. Never days -- "2 d" hides whether that was two
 * working days or a weekend, and the screen shows the business figure beside
 * this one to answer exactly that.
 */
export function formatDuration(ms: number | undefined): string {
  if (ms === undefined || Number.isNaN(ms)) return '—'
  if (ms < MINUTE) return '< 1 min'
  const minutes = Math.round(ms / MINUTE)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')} min`
}

/** Hours with one decimal, for totals of bench time. */
export function formatHours(ms: number | undefined): string {
  if (ms === undefined) return '—'
  return `${(ms / (60 * MINUTE)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`
}

/** `part` of `whole` as a whole percentage, or a dash when there is no whole. */
export function formatShare(part: number, whole: number): string {
  if (whole <= 0) return '—'
  return `${Math.round((part / whole) * 100)}%`
}

/**
 * The relative change from `before` to `after`, signed, for the comparison
 * column. A dash when either side has nothing to compare.
 */
export function formatChange(before: number | undefined, after: number | undefined): string {
  if (before === undefined || after === undefined || before === 0) return '—'
  const change = Math.round(((after - before) / before) * 100)
  return `${change > 0 ? '+' : ''}${change}%`
}

/** `YYYY-MM-DD` of a local date, the form the API takes. */
export function localDay(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * The two windows the screen opens on: the last thirty full days, and the
 * thirty before them. Yesterday is the last day, because today's conferências
 * are still going through the bench and would read as open.
 */
export function defaultWindows(today: Date): {
  before: { from: string; to: string }
  after: { from: string; to: string }
} {
  const day = (offset: number) => {
    const d = new Date(today)
    d.setDate(d.getDate() + offset)
    return localDay(d)
  }
  return {
    before: { from: day(-60), to: day(-31) },
    after: { from: day(-30), to: day(-1) },
  }
}
